/**
 * Bidirectional bridge between legacy flat tokens (CRM, ORDERS, …) and WS-2403
 * `section.*` keys so routes, menu, and team UI stay consistent without duplicating
 * every permission on each role in the database.
 *
 * Rules:
 * - WINERY_ADMIN implies any permission check.
 * - Legacy token X grants any `section.*` check whose key lives under the mapped prefixes.
 * - Any granted `section.*` key implies its legacy token for matching `hasPermission("CRM")`-style checks.
 * - Comparison is case-insensitive (JWT / Spring authorities are uppercased).
 */

const WINERY_ADMIN = "WINERY_ADMIN";

/** Legacy token → `section` path prefixes (first segment after `section` is the area). */
const LEGACY_TO_SECTION_PREFIXES: Record<string, readonly string[]> = {
  CRM: ["section.crm"],
  ORDERS: ["section.sales"],
  DASHBOARD: ["section.dashboard", "section.reports_dashboard"],
  CALENDAR: ["section.calendar"],
  MARKETING: ["section.marketing"],
};

function norm(value: string): string {
  return value.toUpperCase();
}

function grantedHasSectionUnderPrefixes(
  granted: readonly string[],
  prefixes: readonly string[]
): boolean {
  for (const g of granted) {
    const gNorm = norm(g);
    for (const pre of prefixes) {
      const pNorm = norm(pre);
      if (gNorm === pNorm || gNorm.startsWith(`${pNorm}.`)) {
        return true;
      }
    }
  }
  return false;
}

function legacyTokenImpliedBySectionGrants(
  legacy: string,
  granted: readonly string[]
): boolean {
  const prefixes = LEGACY_TO_SECTION_PREFIXES[norm(legacy)]
    ?? LEGACY_TO_SECTION_PREFIXES[legacy];
  if (!prefixes) {
    return false;
  }
  return grantedHasSectionUnderPrefixes(granted, prefixes);
}

function sectionPermissionImpliedByLegacy(
  required: string,
  grantedSet: Set<string>
): boolean {
  const requiredNorm = norm(required);
  if (!requiredNorm.startsWith("SECTION.")) {
    return false;
  }
  for (const [legacy, prefixes] of Object.entries(LEGACY_TO_SECTION_PREFIXES)) {
    if (!grantedSet.has(norm(legacy))) {
      continue;
    }
    if (
      prefixes.some((p) => {
        const pNorm = norm(p);
        return requiredNorm === pNorm || requiredNorm.startsWith(`${pNorm}.`);
      })
    ) {
      return true;
    }
  }
  return false;
}

/**
 * True if `required` is satisfied by `granted` (exact match, WINERY_ADMIN, or legacy↔section bridge).
 */
export function isPermissionGrantedWithLegacyBridge(
  required: string,
  granted: readonly string[]
): boolean {
  if (!granted.length) {
    return false;
  }
  const requiredNorm = norm(required);
  const set = new Set(granted.map(norm));
  if (set.has(requiredNorm)) {
    return true;
  }
  if (set.has(WINERY_ADMIN)) {
    return true;
  }
  if (sectionPermissionImpliedByLegacy(required, set)) {
    return true;
  }
  if (legacyTokenImpliedBySectionGrants(required, granted)) {
    return true;
  }
  return false;
}
